import type { Viewport } from './coach-mark-placement';

export interface SpotlightHole {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;

/** One padded hole per resolved anchor rect — unresolved (`null`) anchors are dropped, not zeroed. */
export function computeSpotlightHoles(
  anchorRects: readonly (DOMRect | null)[],
  viewport: Viewport,
): SpotlightHole[] {
  return anchorRects
    .filter((rect): rect is DOMRect => rect !== null)
    .map((rect) => pad(rect, viewport));
}

function pad(rect: DOMRect, viewport: Viewport): SpotlightHole {
  const left = Math.max(0, rect.left - PADDING);
  const top = Math.max(0, rect.top - PADDING);
  const right = Math.min(viewport.width, rect.right + PADDING);
  const bottom = Math.min(viewport.height, rect.bottom + PADDING);
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * An SVG path covering the whole viewport with one rectangular subpath per hole cut out of it —
 * rendered with `fill-rule="evenodd"`, this darkens everything except the holes, however many
 * there are, without the band-tiling that only works for a single hole.
 */
export function buildSpotlightMaskPath(
  holes: readonly SpotlightHole[],
  viewport: Viewport,
): string {
  const outer = `M0,0 H${viewport.width} V${viewport.height} H0 Z`;
  const cutouts = holes.map(
    (h) => `M${h.left},${h.top} H${h.left + h.width} V${h.top + h.height} H${h.left} Z`,
  );
  return [outer, ...cutouts].join(' ');
}
